<template>
  <aside
    class="fixed top-12 left-0 h-[calc(100vh-3rem)] bg-white border-r border-gray-200 flex flex-col overflow-hidden transition-all duration-300 ease-in-out"
    :class="[
      isSidebarCollapsed ? 'w-16' : 'w-60',
      !isMobileSidebarOpen ? '-translate-x-full md:translate-x-0' : 'translate-x-0'
    ]"
  >
    <div class="border-b border-gray-200 flex-shrink-0 flex items-center" :class="isSidebarCollapsed ? 'justify-center' : 'justify-end'">
      <button
        @click="toggleCollapsed"
        class="flex items-center px-3 py-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors duration-150"
        :title="isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
      >
        <component :is="isSidebarCollapsed ? ChevronsRight : ChevronsLeft" class="w-4 h-4" />
      </button>
    </div>

    <div class="flex-1 min-h-0 overflow-y-auto">
      <div class="flex flex-col min-h-full">
        <div class="px-2 pt-4 pb-2">
          <button
            v-if="!isSidebarCollapsed"
            @click="isPlatformsExpanded = !isPlatformsExpanded"
            class="flex items-center justify-between w-full px-2 group cursor-pointer transition-all duration-300"
          >
            <span class="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
              Platforms
            </span>
            <ChevronDown
              class="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600 transition-transform duration-300"
              :class="{ '-rotate-90': !isPlatformsExpanded }"
            />
          </button>
          <span
            v-else
            class="text-[11px] font-semibold uppercase tracking-wider text-gray-400 px-2 block opacity-0 h-0 mb-0"
          >
            Platforms
          </span>
          <div
            class="grid transition-all duration-300 ease-in-out"
            :class="isPlatformsExpanded || isSidebarCollapsed ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'"
          >
            <ul
class="flex flex-col gap-0.5 overflow-hidden transition-all duration-300 ease-in-out"
            :class="isPlatformsExpanded && !isSidebarCollapsed ? 'pt-2' : ''"
            >
              <li v-for="link in platformLinks" :key="link.name">
                <a
                  :href="link.path"
                  :title="isSidebarCollapsed ? link.name : undefined"
                  class="flex items-center gap-3 px-2 py-2 text-sm text-gray-600 rounded-lg hover:bg-gray-100 transition-all duration-300"
                  :class="[{ 'bg-gray-100 font-medium text-gray-900': link.active }, isSidebarCollapsed ? '!gap-0 pl-2.5' : '']"
                >
                  <span
                    class="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                    :class="link.active ? 'bg-cosmicPurple' : 'bg-gray-800'"
                  >
                    <img :src="link.icon" class="w-3.5 h-3.5 brightness-0 invert" alt="" />
                  </span>
                  <span
                    class="whitespace-nowrap overflow-hidden transition-all duration-300"
                    :class="isSidebarCollapsed ? 'opacity-0 max-w-0' : 'opacity-100 max-w-[10rem]'"
                  >
                    {{ link.name }}
                  </span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        <hr class="mx-3 my-2 border-gray-200" />

        <div class="px-2 pb-2">
          <template v-for="(section, sectionIndex) in navigationSections" :key="section.label">
            <span
              class="text-[11px] font-semibold uppercase tracking-wider text-gray-400 px-2 block overflow-hidden transition-all duration-300"
              :class="[isSidebarCollapsed ? 'opacity-0 h-0 mb-0' : 'opacity-100 h-4 mb-2', sectionIndex > 0 ? 'mt-4' : '']"
            >
              {{ section.label }}
            </span>
            <ul class="flex flex-col gap-0.5">
              <li v-for="link in section.links" :key="link.name">
                <router-link
                  :to="link.path"
                  :title="isSidebarCollapsed ? link.name : undefined"
                  class="flex items-center gap-3 px-2 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-100 transition-all duration-300"
                  :class="[{ 'bg-cosmicPurple/10 font-medium text-cosmicPurple': isRouteActive(link.path) }, isSidebarCollapsed ? '!gap-0 pl-3.5' : '']"
                  @click="closeMobileSidebar"
                >
                  <component
                    :is="link.icon"
                    class="w-5 h-5 shrink-0"
                    :class="isRouteActive(link.path) ? 'text-cosmicPurple' : 'text-gray-400'"
                  />
                  <span
                    class="whitespace-nowrap overflow-hidden transition-all duration-300"
                    :class="isSidebarCollapsed ? 'opacity-0 max-w-0' : 'opacity-100 max-w-[10rem]'"
                  >
                    {{ link.name }}
                  </span>
                </router-link>
              </li>
            </ul>
          </template>

          <!-- New application shortcut — limited users only -->
          <ul v-if="isLimitedUser" class="flex flex-col gap-0.5">
            <li>
              <router-link
                to="/applications/create-new"
                :title="isSidebarCollapsed ? 'New application' : undefined"
                class="flex items-center gap-3 px-2 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-100 transition-all duration-300"
                :class="[{ 'bg-cosmicPurple/10 font-medium text-cosmicPurple': isRouteActive('/applications/create-new') }, isSidebarCollapsed ? '!gap-0 pl-3.5' : '']"
                @click="closeMobileSidebar"
              >
                <Plus
                  class="w-5 h-5 shrink-0"
                  :class="isRouteActive('/applications/create-new') ? 'text-cosmicPurple' : 'text-gray-400'"
                />
                <span
                  class="whitespace-nowrap overflow-hidden transition-all duration-300"
                  :class="isSidebarCollapsed ? 'opacity-0 max-w-0' : 'opacity-100 max-w-[10rem]'"
                >
                  New Application
                </span>
              </router-link>
            </li>
          </ul>
        </div>

        <DashboardFooter :collapsed="isSidebarCollapsed" class="mt-auto" />
      </div>
    </div>
  </aside>
</template>

<script setup>
import { inject, computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useAuth } from '@/composables/useAuth'
import { usePrivacy } from '@/composables/usePrivacy'
import {
  Files,
  Briefcase,
  Users,
  Building,
  CircleUser,
  Send,
  Handshake,
  FileText,
  BarChart3,
  ChevronsLeft,
  ChevronsRight,
  ChevronDown,
  Plus
} from 'lucide-vue-next'
import { platformLinks as sharedPlatformLinks, useSidebarStateSync } from '@outtalent-dev/shared'
import DashboardFooter from '@/components/layout/DashboardFooter.vue'

const route = useRoute()
const { isGuide, isReferrer, isLimitedUser, isAdmin } = useAuth()
const { isBlurred } = usePrivacy()
const isMobileSidebarOpen = inject('isMobileSidebarOpen')
const isSidebarCollapsed = inject('isSidebarCollapsed')
const isPlatformsExpanded = ref(localStorage.getItem('platformsExpanded') !== 'false')
watch(isPlatformsExpanded, (val) => localStorage.setItem('platformsExpanded', String(val)))

const { platformLinksWithState: platformLinks } = useSidebarStateSync({
  isSidebarCollapsed: isSidebarCollapsed ?? ref(false),
  isPlatformsExpanded,
  isBlurred,
  links: sharedPlatformLinks,
  isActive: (link) => link.name === 'TrackTalent'
})

// Limited users (Common role or unsupported Fellows) get a reduced nav:
// Job Opportunities, Companies, My Applications only
const commonNavigationLinks = computed(() => {
  if (isLimitedUser.value) {
    return [
      { name: 'Job Opportunities', path: '/jobs', icon: Briefcase },
      { name: 'Companies', path: '/companies', icon: Building },
      { name: 'My Applications', path: '/applications', icon: Files }
    ]
  }

  const items = [
    { name: 'Job Opportunities', path: '/jobs', icon: Briefcase },
    { name: 'Companies', path: '/companies', icon: Building },
    { name: 'My Applications', path: '/applications', icon: Files },
    { name: 'My Applicant Profile', path: '/applicant', icon: CircleUser },
    { name: 'Fellow Applicants', path: '/applicants', icon: Users }
  ]

  // Guides don't need their own applicant profile link
  return isGuide.value ? items.filter((item) => item.path !== '/applicant') : items
})

const guideNavigationLinks = computed(() => {
  const links = [
    { name: 'All Job Applications', path: '/applications/all', icon: Files },
    { name: 'Job Search Progress', path: '/job-search-progress', icon: BarChart3 },
    // Referrals & Referring Contacts are admin-only
    ...(isAdmin.value
      ? [
          { name: 'Referrals', path: '/referral-requests', icon: Send },
          { name: 'Referring Contacts', path: '/referrers', icon: Handshake }
        ]
      : []),
    { name: 'Active Applicants', path: '/applicants/recently-active', icon: Users },
    { name: 'Resumes', path: '/resumes', icon: FileText }
  ]
  return links
})

// Groups the nav into labeled sections; Guides get their own tools separated
// from the general/common links, everyone else gets a single section.
const navigationSections = computed(() => {
  const referrerItem = { name: 'My Referrals', path: '/my-referrals', icon: Send }

  if (isGuide.value) {
    const guideLinks = isReferrer.value
      ? [referrerItem, ...guideNavigationLinks.value]
      : guideNavigationLinks.value
    return [
      { label: 'General', links: commonNavigationLinks.value },
      { label: 'Guide Tools', links: guideLinks }
    ]
  }

  // Only full-access users (non-limited) can see My Referrals
  const links = isReferrer.value && !isLimitedUser.value
    ? [referrerItem, ...commonNavigationLinks.value]
    : commonNavigationLinks.value

  return [{ label: 'TrackTalent', links }]
})

const isRouteActive = (path) => {
  if (path === '/applications') {
    return route.path.startsWith('/applications')
      && !route.path.startsWith('/applications/all')
      && !route.path.startsWith('/applications/create-new')
  }
  if (path === '/applications/all') {
    return route.path.startsWith('/applications/all')
  }
  if (path === '/applicants') {
    return (
      (route.path.startsWith('/applicants') || route.path.startsWith('/applicant/')) &&
      !route.path.startsWith('/applicants/recently-active')
    )
  }
  if (path === '/referrers') {
    return route.path.startsWith('/referrers')
  }
  if (path === '/my-referrals') {
    return route.path.startsWith('/my-referrals')
  }
  if (path === '/applicant') {
    return route.path.endsWith('/applicant')
  }
  if (path === '/candidates/latest-updates') {
    return route.path.startsWith('/candidates/latest-updates')
  }
  if (path.startsWith('/applicant')) {
    return route.path.startsWith(path)
  }
  return route.path.startsWith(path)
}

function toggleCollapsed() {
  if (isSidebarCollapsed) {
    isSidebarCollapsed.value = !isSidebarCollapsed.value
  }
}

function closeMobileSidebar() {
  if (isMobileSidebarOpen?.value) {
    isMobileSidebarOpen.value = false
  }
}
</script>
